import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Select.scss';

export type SelectOption<T extends string = string> = {
  id: T;
  label: string;
  icon?: React.ReactNode; // опційно іконка зліва
};

interface SelectProps<T extends string = string> {
  options: SelectOption<T>[];
  value?: T;
  placeholder?: string;
  className?: string;
  onChange?: (value: T, option: SelectOption<T>) => void;
  ariaLabel?: string; // якщо без видимого label
  maxMenuHeight?: number;
}

export function Select<T extends string = string>({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  className = '',
  ariaLabel,
  maxMenuHeight = 280,
}: SelectProps<T>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const selected = useMemo(() => options.find((o) => o.id === value), [options, value]);
  const label = selected?.label ?? placeholder;

  // закриття по кліку назовні
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // клавіатура
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(
        Math.max(
          0,
          options.findIndex((o) => o.id === value),
        ),
      );
      return;
    }
    if (!open) return;

    if (e.key === 'Escape') {
      setOpen(false);
      btnRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + options.length) % options.length);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const opt = options[activeIndex];
      if (opt) {
        onChange?.(opt.id, opt);
        setOpen(false);
        btnRef.current?.focus();
      }
    }
  };

  return (
    <div ref={wrapRef} className={`ui-select ${className}`}>
      <button
        ref={btnRef}
        type="button"
        className={`ui-select__trigger ${open ? 'is-open' : ''}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
      >
        <span className="ui-select__text">{label}</span>
        <span className="ui-select__caret" aria-hidden>
          <svg viewBox="0 0 24 24">
            <path d="M7 10l5 5 5-5" fill="currentColor" />
          </svg>
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="ui-select__menu"
          style={{ maxHeight: maxMenuHeight }}
          onKeyDown={onKeyDown}
        >
          {options.map((opt, idx) => {
            const isSelected = opt.id === value;
            const isActive = idx === activeIndex;
            return (
              <li className="ui-select__list" role="presentation" key={opt.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={[
                    'ui-select__option',
                    isSelected ? 'is-selected' : '',
                    isActive ? 'is-active' : '',
                  ].join(' ')}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => {
                    onChange?.(opt.id, opt);
                    setOpen(false);
                    btnRef.current?.focus();
                  }}
                >
                  {opt.icon && <span className="ui-select__icon">{opt.icon}</span>}
                  <span>{opt.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
